import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { 
  Users, Car, TrendingUp, CreditCard, Settings, 
  RefreshCw, Activity, Database, Shield, Wrench, DollarSign, AlertTriangle, Building2, Radio, Navigation, Search, ShieldAlert
} from 'lucide-react';
import { AdminOverviewTab } from '../components/admin/AdminOverviewTab';
import { AdminUsersTab } from '../components/admin/AdminUsersTab';
import { AdminRidesTab } from '../components/admin/AdminRidesTab';
import { AdminTransactionsTab } from '../components/admin/AdminTransactionsTab';
import { AdminSettingsTab } from '../components/admin/AdminSettingsTab';
import { AdminPayoutsTab } from '../components/admin/AdminPayoutsTab';
import { AdminDisputesTab } from '../components/admin/AdminDisputesTab';
import AdminVerificationsTab from '../components/admin/AdminVerificationsTab';
import { AdminSOSDashboardTab } from '../components/admin/AdminSOSDashboardTab';
import { AdminAnalyticsTab } from '../components/admin/AdminAnalyticsTab';
import { AdminB2BOrgsTab } from '../components/admin/AdminB2BOrgsTab';
import { AdminGuardianFeedTab } from '../components/admin/AdminGuardianFeedTab';
import { AdminB2BDashboardTab } from '../components/admin/AdminB2BDashboardTab';
import { AdminSearchInsightsTab } from '../components/admin/AdminSearchInsightsTab';
import { AdminCommuterTab } from '../components/admin/AdminCommuterTab';
import { AdminVehicleSegmentsTab } from '../components/admin/AdminVehicleSegmentsTab';
import { AdminB2BBillingTab } from '../components/admin/AdminB2BBillingTab';
import { AdminFaceMatchTab } from '../components/admin/AdminFaceMatchTab';
import './AdminDashboard.css';

import { BACKEND_URL, API } from '../utils/apiConfig';

export const AdminDashboard = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [rides, setRides] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [revenue, setRevenue] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [updatingStats, setUpdatingStats] = useState(false);
  const [maintenanceRunning, setMaintenanceRunning] = useState(false);

  const [priceSettings, setPriceSettings] = useState({ min_per_km: 0.05, recommended_per_km: 0.06, max_per_km: 0.075 });
  const [settingsMessage, setSettingsMessage] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    loadDashboardData();
    loadPriceSettings();
    // No auto-refresh polling — manual reload only via Reload button
    // This prevents zombie requests when navigating away
  }, []);

  const getUserIdFromToken = (token) => {
    try {
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub || payload.user_id || payload.id;
    } catch (e) { return null; }
  };

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const loadDashboardData = async () => {
    setLoading(true);
    const headers = getHeaders();
    try {
      const [statsRes, usersRes, ridesRes, revenueRes, transRes] = await Promise.allSettled([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users?limit=50`, { headers }),
        axios.get(`${API}/admin/rides/all?limit=50`, { headers }),
        axios.get(`${API}/admin/revenue/summary`, { headers }),
        axios.get(`${API}/admin/transactions?limit=50`, { headers }),
      ]);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (usersRes.status === 'fulfilled') setUsers(usersRes.value.data?.users || usersRes.value.data || []);
      if (ridesRes.status === 'fulfilled') { const d = ridesRes.value.data?.rides || ridesRes.value.data || []; setRides(Array.isArray(d) ? d : []); }
      if (revenueRes.status === 'fulfilled') setRevenue(revenueRes.value.data);
      else setRevenue({ total_volume: 0, pending_amount: 0, refunded_amount: 0, total_revenue: 0 });
      if (transRes.status === 'fulfilled') setTransactions(transRes.value.data?.transactions || transRes.value.data || []);
    } catch (error) {
      if (error.response?.status === 403) alert(t('admin.noPermission'));
      else if (error.response?.status === 401) alert(t('admin.sessionExpired'));
    } finally {
      setLoading(false);
    }
  };

  const loadPriceSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/price-settings`, { headers: getHeaders() });
      if (response.data) setPriceSettings(response.data);
    } catch (error) { /* use defaults */ }
  };

  const updateAllDriverStats = async () => {
    if (!window.confirm(t('admin.confirm.updateDriverStats'))) return;
    setUpdatingStats(true);
    try {
      const response = await axios.post(`${API}/admin/update-driver-stats/all`, {}, { headers: getHeaders() });
      alert(`${t('admin.success.driverStatsUpdated')}\n${t('admin.success.driversUpdated')}: ${response.data.drivers_updated}`);
      setTimeout(loadDashboardData, 1000);
    } catch (error) {
      alert(t('admin.error.updateDriverStats') + ': ' + (error.response?.data?.detail || error.message));
    } finally { setUpdatingStats(false); }
  };

  const runMaintenanceTasks = async () => {
    if (!window.confirm(t('admin.confirm.runMaintenance'))) return;
    setMaintenanceRunning(true);
    try {
      await axios.post(`${API}/admin/run-maintenance`, {}, { headers: getHeaders() });
      alert(t('admin.success.maintenanceStarted'));
      setTimeout(loadDashboardData, 5000);
    } catch (error) {
      alert(t('admin.error.runMaintenance') + ': ' + (error.response?.data?.detail || error.message));
    } finally { setMaintenanceRunning(false); }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    setSettingsMessage(null);
    if (priceSettings.min_per_km >= priceSettings.recommended_per_km) {
      setSettingsMessage({ type: 'error', text: t('admin.settings.minLessThanRecommended') });
      setSavingSettings(false); return;
    }
    if (priceSettings.recommended_per_km >= priceSettings.max_per_km) {
      setSettingsMessage({ type: 'error', text: t('admin.settings.recommendedLessThanMax') });
      setSavingSettings(false); return;
    }
    try {
      await axios.put(`${API}/admin/price-settings`, priceSettings, { headers: getHeaders() });
      setSettingsMessage({ type: 'success', text: t('admin.success.settingsSaved') });
    } catch (error) {
      setSettingsMessage({ type: 'error', text: t('admin.error.saveFailed') });
    } finally { setSavingSettings(false); }
  };

  const resetToDefaults = () => {
    if (window.confirm(t('admin.confirm.resetDefaults'))) {
      setPriceSettings({ min_per_km: 0.05, recommended_per_km: 0.06, max_per_km: 0.075 });
    }
  };

  const handleUserUpdate = async () => {
    if (!editingUser) return;
    try {
      await axios.put(`${API}/admin/users/${editingUser.id}`, editingUser, { headers: getHeaders() });
      setEditingUser(null);
      loadDashboardData();
    } catch (err) { alert(t('admin.error.saveUserFailed') + ': ' + (err.response?.data?.detail || err.message)); }
  };

  const handleBanUser = async (userId, currentlyBanned) => {
    const action = currentlyBanned ? 'unban' : 'ban';
    if (!window.confirm(currentlyBanned ? t('admin.confirm.unbanUser') : t('admin.confirm.banUser'))) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/${action}`, {}, { headers: getHeaders() });
      alert(currentlyBanned ? t('admin.users.userUnbanned') : t('admin.users.userBanned'));
      loadDashboardData();
    } catch (error) { alert(t('admin.error.banFailed') + ': ' + (error.response?.data?.detail || error.message)); }
  };

  const handleVerifyToggle = async (userId, shouldVerify) => {
    const confirmMsg = shouldVerify ? 'Verify this user\'s identity?' : 'Remove identity verification?';
    if (!window.confirm(confirmMsg)) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/verify`, {
        identity_verified: shouldVerify,
        verification_provider: 'Admin'
      }, { headers: getHeaders() });
      alert(shouldVerify ? 'User verified' : 'Verification removed');
      loadDashboardData();
    } catch (error) { alert('Verification toggle failed: ' + (error.response?.data?.detail || error.message)); }
  };

  const handleDeleteRide = async (rideId) => {
    if (!window.confirm(t('admin.rides.confirmDelete'))) return;
    try {
      await axios.delete(`${API}/admin/rides/${rideId}`, { headers: getHeaders() });
      loadDashboardData();
    } catch (error) { alert(t('admin.error.deleteFailed') + ': ' + (error.response?.data?.detail || error.message)); }
  };

  const testEndpoints = async () => {
    const headers = getHeaders();
    const endpoints = [
      { name: 'Stats', url: `${API}/admin/stats` },
      { name: 'Users', url: `${API}/admin/users?limit=5` },
      { name: 'Rides', url: `${API}/admin/rides/all?limit=5` },
      { name: 'Revenue', url: `${API}/admin/revenue/summary` },
      { name: 'Transactions', url: `${API}/admin/transactions?limit=5` },
    ];
    const results = [];
    for (const ep of endpoints) {
      try {
        const res = await axios.get(ep.url, { headers, timeout: 5000 });
        results.push(`OK ${ep.name} (${res.status})`);
      } catch (error) {
        results.push(`FAIL ${ep.name}: ${error.response?.status || error.message}`);
      }
    }
    alert('API Test:\n\n' + results.join('\n'));
  };

  if (loading) {
    return (
      <div className="admin-dashboard" data-testid="admin-dashboard">
        <div className="loading-state"><div className="spinner"></div><p>{t('admin.loading')}</p></div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard" data-testid="admin-dashboard">
      <div className="admin-header">
        <div className="header-top">
          <h1>{t('admin.title')}</h1>
          <div className="header-actions">
            <button onClick={updateAllDriverStats} className="admin-action-btn stats" disabled={updatingStats}>
              <Database size={16} /> {updatingStats ? t('admin.actions.updating') : t('admin.actions.driverStats')}
            </button>
            <button onClick={runMaintenanceTasks} className="admin-action-btn maintenance" disabled={maintenanceRunning}>
              <Wrench size={16} /> {maintenanceRunning ? t('admin.actions.running') : t('admin.actions.maintenance')}
            </button>
            <button onClick={testEndpoints} className="admin-action-btn debug">
              <Activity size={16} /> {t('admin.actions.apiTest')}
            </button>
            <button onClick={loadDashboardData} className="refresh-btn">
              <RefreshCw size={16} /> {t('admin.actions.reload')}
            </button>
          </div>
        </div>
        <div className="header-subtitle">
          <p>{t('admin.systemOverview')}</p>
          {stats && (
            <div className="quick-stats">
              <span>{stats.total_users || 0} {t('admin.tabs.users')}</span>
              <span>{stats.total_rides || 0} {t('admin.tabs.rides')}</span>
              <span>{revenue?.total_volume || 0}€ {t('admin.tabs.revenue')}</span>
            </div>
          )}
        </div>
      </div>

      <div className="admin-tabs">
        <button className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
          <TrendingUp size={18} /> {t('admin.tabs.overview')}
        </button>
        <button className={`tab-button ${activeTab === 'analytics' ? 'active' : ''}`} onClick={() => setActiveTab('analytics')} data-testid="admin-analytics-tab-btn">
          <Activity size={18} /> {t('admin.tabs.analytics')}
        </button>
        <button className={`tab-button ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>
          <Users size={18} /> {t('admin.tabs.users')} ({users.length})
        </button>
        <button className={`tab-button ${activeTab === 'rides' ? 'active' : ''}`} onClick={() => setActiveTab('rides')}>
          <Car size={18} /> {t('admin.tabs.rides')} ({rides.length})
        </button>
        <button className={`tab-button ${activeTab === 'transactions' ? 'active' : ''}`} onClick={() => setActiveTab('transactions')}>
          <CreditCard size={18} /> {t('admin.tabs.transactions')}
        </button>
        <button className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
          <Settings size={18} /> {t('admin.tabs.settings')}
        </button>
        <button className={`tab-button ${activeTab === 'payouts' ? 'active' : ''}`} onClick={() => setActiveTab('payouts')} data-testid="admin-payouts-tab-btn">
          <DollarSign size={18} /> {t('admin.tabs.payouts')}
        </button>
        <button className={`tab-button ${activeTab === 'disputes' ? 'active' : ''}`} onClick={() => setActiveTab('disputes')} data-testid="admin-disputes-tab-btn">
          <AlertTriangle size={18} /> {t('disputes.title')}
        </button>
        <button className={`tab-button ${activeTab === 'verifications' ? 'active' : ''}`} onClick={() => setActiveTab('verifications')} data-testid="admin-verifications-tab-btn">
          <Shield size={18} /> {t('admin.tabs.verifications')}
        </button>
        <button className={`tab-button ${activeTab === 'b2b-orgs' ? 'active' : ''}`} onClick={() => setActiveTab('b2b-orgs')} data-testid="admin-b2b-orgs-tab-btn">
          <Building2 size={18} /> {t('b2b.tab', 'B2B Organisationen')}
        </button>
        <button className={`tab-button ${activeTab === 'b2b-dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('b2b-dashboard')} data-testid="admin-b2b-dashboard-tab-btn">
          <Navigation size={18} /> {t('b2b.adminDashboard', 'B2B Dashboard')}
        </button>
        <button className={`tab-button ${activeTab === 'guardian' ? 'active' : ''}`} onClick={() => setActiveTab('guardian')} data-testid="admin-guardian-tab-btn">
          <Radio size={18} /> Guardian Feed
        </button>
        <button className={`tab-button ${activeTab === 'search-insights' ? 'active' : ''}`} onClick={() => setActiveTab('search-insights')} data-testid="admin-search-insights-tab-btn">
          <Search size={18} /> Search Insights
        </button>
        <button className={`tab-button ${activeTab === 'commuter' ? 'active' : ''}`} onClick={() => setActiveTab('commuter')} data-testid="admin-commuter-tab-btn">
          <Navigation size={18} /> Pendler
        </button>
        <button className={`tab-button ${activeTab === 'kba' ? 'active' : ''}`} onClick={() => setActiveTab('kba')} data-testid="admin-kba-tab-btn">
          <Car size={18} /> {t('admin.kba.kbaTab')}
        </button>
        <button className={`tab-button ${activeTab === 'b2b-billing' ? 'active' : ''}`} onClick={() => setActiveTab('b2b-billing')} data-testid="admin-b2b-billing-tab-btn">
          <CreditCard size={18} /> {t('admin.b2bBilling.tab', 'B2B-Billing')}
        </button>
        <button className={`tab-button ${activeTab === 'face-match' ? 'active' : ''}`} onClick={() => setActiveTab('face-match')} data-testid="admin-face-match-tab-btn">
          <Shield size={18} /> {t('admin.faceMatch.tab', 'Face-Match')}
        </button>
        <button className={`tab-button ${activeTab === 'sos' ? 'active' : ''}`} onClick={() => setActiveTab('sos')} data-testid="admin-sos-tab-btn">
          <ShieldAlert size={18} /> {t('admin.sos.tab', 'Notrufe')}
        </button>
      </div>

      {activeTab === 'sos' && <AdminSOSDashboardTab />}

      {activeTab === 'overview' && stats && (
        <AdminOverviewTab
          stats={stats} revenue={revenue}
          updatingStats={updatingStats} maintenanceRunning={maintenanceRunning}
          updateAllDriverStats={updateAllDriverStats} runMaintenanceTasks={runMaintenanceTasks}
          testEndpoints={testEndpoints}
        />
      )}

      {activeTab === 'analytics' && (
        <AdminAnalyticsTab stats={stats} revenue={revenue} />
      )}

      {activeTab === 'users' && (
        <AdminUsersTab
          users={users} searchTerm={searchTerm} setSearchTerm={setSearchTerm}
          editingUser={editingUser} setEditingUser={setEditingUser}
          handleUserUpdate={handleUserUpdate} handleBanUser={handleBanUser} handleVerifyToggle={handleVerifyToggle}
          updatingStats={updatingStats} updateAllDriverStats={updateAllDriverStats}
          loadDashboardData={loadDashboardData} testEndpoints={testEndpoints}
        />
      )}

      {activeTab === 'rides' && (
        <AdminRidesTab
          rides={rides} searchTerm={searchTerm} setSearchTerm={setSearchTerm}
          handleDeleteRide={handleDeleteRide} loadDashboardData={loadDashboardData}
        />
      )}

      {activeTab === 'transactions' && (
        <AdminTransactionsTab
          transactions={transactions} revenue={revenue} loadDashboardData={loadDashboardData}
        />
      )}

      {activeTab === 'settings' && (
        <AdminSettingsTab
          priceSettings={priceSettings} setPriceSettings={setPriceSettings}
          settingsMessage={settingsMessage} savingSettings={savingSettings}
          handleSaveSettings={handleSaveSettings} resetToDefaults={resetToDefaults}
        />
      )}

      {activeTab === 'payouts' && (
        <AdminPayoutsTab />
      )}

      {activeTab === 'disputes' && (
        <AdminDisputesTab />
      )}

      {activeTab === 'verifications' && (
        <AdminVerificationsTab />
      )}

      {activeTab === 'b2b-orgs' && (
        <AdminB2BOrgsTab />
      )}

      {activeTab === 'b2b-dashboard' && (
        <AdminB2BDashboardTab />
      )}

      {activeTab === 'guardian' && (
        <AdminGuardianFeedTab />
      )}

      {activeTab === 'search-insights' && (
        <AdminSearchInsightsTab />
      )}

      {activeTab === 'commuter' && (
        <AdminCommuterTab />
      )}

      {activeTab === 'kba' && (
        <AdminVehicleSegmentsTab />
      )}

      {activeTab === 'b2b-billing' && (
        <AdminB2BBillingTab />
      )}

      {activeTab === 'face-match' && (
        <AdminFaceMatchTab />
      )}
    </div>
  );
};
